import { realtimeEventSchema } from '@sentra/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { adminKeys } from '@/api/admin-keys';
import { hasCode, isApiError } from '@/api/errors';
import { queryKeys } from '@/api/query-keys';
import { streamRoutes } from '@/api/routes';
import { useApiClient } from '@/features/auth/session';
import { applyRealtimeFrame } from '@/features/realtime/apply';
import type { VisibilitySource } from '@/features/realtime/coalesce';
import { createInvalidationCoalescer } from '@/features/realtime/coalesce';

/**
 * AD-57's stream, from the browser's side of it.
 *
 * The hub, the route, the contract, the anti-buffering header and a dedicated `location` block in
 * BOTH Nginx layers have all existed since the stream was written. What did not exist was anybody
 * listening: the occupancy figure an operator looks at was a sixty-second poll while a frame
 * describing it was published on every movement.
 *
 * **It is open only while somebody is at the terminal.** The same predicate the idle lock uses, for
 * two reasons: a locked terminal must not be drawing anything behind its overlay, and coming back
 * from the lock then produces a genuinely current picture rather than a replay of what was missed.
 *
 * **The backoff is a function of the attempt and carries no jitter.**
 * `tools/testing/__tests__/determinism-guard.test.mjs` refuses the platform's random generator
 * anywhere in this repository's code, and `packages/server/src/db/tx.ts` already met this trade and
 * resolved it the same way. Jitter buys little here in any case: this is a handful of terminals in
 * one facility, not a fleet whose simultaneous reconnect would matter. The delay and the timer are
 * both injectable, exactly as the server hub's own `schedule` is, because the `web` tier freezes
 * `Date` and leaves timers real — so a backoff proved by sleeping would be a sleep used as
 * synchronisation, which `audit:integrity` refuses by name.
 */

/** How long to wait before attempt `n`, capped. Doubling, so a dead api is asked for rarely. */
export function backoffFor(attempt: number): number {
  const ceiling = 30_000;
  return Math.min(1000 * 2 ** Math.max(attempt - 1, 0), ceiling);
}

/**
 * How long a connection has to have stayed up for its next reconnect to start the backoff afresh.
 *
 * A connection that lived this long was a healthy one that a restart, a drain or a proxy closed;
 * one that ended sooner is a server still refusing or still coming back, which is what the backoff
 * is for. Without the reset, the attempt counter only ever rose, and after a few ordinary
 * reconnects over a long shift every later one waited the thirty-second ceiling.
 */
export const HEALTHY_CONNECTION_MS = 30_000;

export interface RealtimeOptions {
  /** Whether somebody is at this terminal. Nothing is opened, or kept open, when false. */
  readonly enabled: boolean;
  /** Test seam. */
  readonly backoffMs?: (attempt: number) => number;
  /** Test seam: returns a cancel. Defaults to `setTimeout`. */
  readonly schedule?: (work: () => void, ms: number) => () => void;
  /** Test seam: a monotonic clock in milliseconds. Defaults to `performance.now`. */
  readonly now?: () => number;
  /** Test seam: whether the document is being looked at. Defaults to the document's own. */
  readonly visibility?: VisibilitySource;
}

function defaultSchedule(work: () => void, ms: number): () => void {
  const handle = globalThis.setTimeout(work, ms);
  return () => {
    globalThis.clearTimeout(handle);
  };
}

/**
 * Whether a failure is worth trying again, and the soonest it may be.
 *
 * **A `4xx` stops the subscription and a `5xx` does not**, and the distinction is the reason this
 * client is not an `EventSource`. A refused stream — an expired session, a role that no longer
 * holds `presence:read` — will be refused identically forever, and retrying one every few seconds
 * spends the coarse per-address budget that a gate's scans share. A `502`, `503` or `504` is the
 * api being restarted or absent, which the inner Nginx answers with this product's own error
 * contract, and that is precisely the case a reconnect is for.
 *
 * **A `429` is the one `4xx` that is retried, and never sooner than it said.** It refuses this
 * address for a while rather than this session for good, so stopping on it — which this client
 * used to do — left a terminal silently without live updates until somebody reloaded it, after
 * every brief rate limit anywhere in the building. Reconnecting before `Retry-After` would only be
 * refused again, so the wait is the larger of the backoff and what the server asked for.
 */
function retryDelayFor(error: unknown, backoff: number): number | null {
  if (hasCode(error, 'SYSTEM_RATE_LIMITED')) {
    const seconds = isApiError(error) ? (error.retryAfterSeconds ?? 0) : 0;
    return Math.max(backoff, seconds * 1000);
  }
  if (isApiError(error)) {
    return error.status >= 500 ? backoff : null;
  }
  // A torn connection, a DNS failure, a proxy that hung up: the transport, which is what a
  // reconnect exists for.
  return backoff;
}

function defaultNow(): number {
  return performance.now();
}

export function useRealtimeStream(options: RealtimeOptions): void {
  const client = useApiClient();
  const queries = useQueryClient();
  const { enabled, visibility } = options;
  const backoffMs = options.backoffMs ?? backoffFor;
  const schedule = options.schedule ?? defaultSchedule;
  const now = options.now ?? defaultNow;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const controller = new AbortController();
    // One per subscription and disposed with it, so a frame that arrives after an unmount or a lock
    // schedules nothing, and a test's pending edge cannot outlive the tree that made it.
    const sink = createInvalidationCoalescer(queries, {
      schedule,
      ...(visibility === undefined ? {} : { visibility }),
    });
    let stopped = false;
    let attempt = 0;
    let cancelTimer: () => void = () => undefined;

    /**
     * AD-47's reconcile-on-reconnect: the durable views, re-read over REST.
     *
     * **On a RECONNECT and not on the first connect.** The screen loaded its own data a moment ago;
     * what a reconnect has to repair is whatever was published while the connection was down, and
     * this stream gives no way to know what that was — deliberately, because a resume that covered
     * one of four event types would look complete. `invalidateQueries` refetches only ACTIVE
     * observers, so this costs exactly the views that are on screen and needs no branch on what the
     * operator is allowed to read.
     *
     * The protocol names three views. The third, the bounded anomalies window, has no reader in
     * this browser — there is no anomalies screen — so two are re-read and the third is named here
     * rather than left to look like an oversight.
     */
    const reconcile = (): void => {
      sink.invalidate(queryKeys.presenceCounts());
      sink.invalidate(adminKeys.alertsPrefix());
    };

    /** When the current connection was asked for, so its end can say whether it was healthy. */
    let startedAt = 0;

    const retry = (error?: unknown): void => {
      if (stopped || controller.signal.aborted) {
        return;
      }
      // A connection that stayed up was a healthy one; what ends it is not a reason to keep
      // waiting longer for the next one.
      if (now() - startedAt >= HEALTHY_CONNECTION_MS) {
        attempt = 0;
      }
      attempt += 1;
      const delay = retryDelayFor(error, backoffMs(attempt));
      if (delay === null) {
        stopped = true;
        return;
      }
      cancelTimer = schedule(run, delay);
    };

    function run(): void {
      if (stopped || controller.signal.aborted) {
        return;
      }
      if (attempt > 0) {
        reconcile();
      }
      startedAt = now();
      client
        .stream(streamRoutes.realtimeStream, {
          signal: controller.signal,
          onFrame: (frame) => {
            // **Parsed against the contract, exactly as every response body is.** A frame that is
            // not the shape `INTEGRATION.md` documents is dropped rather than applied: the stream
            // is fanned out to every terminal, so acting on something unrecognised would be acting
            // on a message this client cannot vouch for.
            let payload: unknown;
            try {
              payload = JSON.parse(frame.data);
            } catch {
              return;
            }
            const parsed = realtimeEventSchema.safeParse({ type: frame.event, data: payload });
            if (parsed.success) {
              applyRealtimeFrame(sink, parsed.data);
            }
          },
        })
        .then(() => {
          // The server closed the stream — a restart, a drain, a proxy timing out an idle
          // connection. Nothing is wrong; the terminal wants it back.
          retry();
        })
        .catch((error: unknown) => {
          retry(error);
        });
    }

    run();

    return () => {
      stopped = true;
      cancelTimer();
      controller.abort();
      sink.dispose();
    };
  }, [client, queries, enabled, backoffMs, schedule, now, visibility]);
}
